import React from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from 'react-router-dom'
import { TenantProvider } from './context/TenantContext'
import { LanguageProvider } from './context/LanguageContext'
import { ClientProvider } from './context/ClientContext'
import { CardProvider } from './context/CardContext'
import { OffersProvider } from './context/OffersContext'
import { GamificationProvider } from './context/GamificationContext'
import { ToastProvider } from './context/ToastContext'
import { ThemeProvider } from './themes/ThemeProvider'
import ProtectedRoute from './components/ProtectedRoute'
import MainLayout from './pages/MainLayout'
import SignIn from './pages/SignIn'
import CreateAccount from './pages/CreateAccount'
import VerifyEmail from './pages/VerifyEmail'
import VerifyEmailToken from './pages/VerifyEmailToken'
import Verify from './pages/Verify'
import ForgotPassword from './pages/ForgotPassword'
import PasswordlessLogin from './pages/PasswordlessLogin'
import ResetPassword from './pages/ResetPassword'
import MigrationRequest from './pages/MigrationRequest'
import SetPassword from './pages/SetPassword'
import ChangePassword from './pages/ChangePassword'
import ChangeEmail from './pages/ChangeEmail'
import ChangeEmailPending from './pages/ChangeEmailPending'
import MyProfile from './pages/MyProfile'
import HealthCheck from './pages/HealthCheck'
import HomePage from './pages/HomePage'
import OfferDetails from './pages/OfferDetails'
import FAQ from './pages/FAQ'
import TermsAndConditions from './pages/TermsAndConditions'
import AboutVoxPage from './pages/AboutVoxPage'
import CookiePolicy from './pages/CookiePolicy'
import ServicePolicy from './pages/ServicePolicy'
import EmailUnsubscribe from './pages/EmailUnsubscribe'
import CardFetchSync from './components/CardFetchSync'
import CookieConsentManager from './components/CookieConsentManager'
import AnalyticsManager from './components/AnalyticsManager'
import MarketingPixelManager from './components/MarketingPixelManager'
import { ABTestProvider } from './context/ABTestContext'
import ABTestManager from './components/ABTestManager'
import SpinWheel from './pages/gamification/SpinWheel'
import CongratulationScreen from './pages/gamification/CongratulationScreen'
import SharingScreen from './pages/gamification/SharingScreen'
import StampRally from './pages/gamification/StampRally'
import InquiryFormMenu from './pages/InquiryFormMenu'
import InquiryForm from './pages/InquiryForm'
import InquiryCategoryForms from './pages/InquiryCategoryForms'
import AccountSettingFormMenu from './pages/AccountSettingFormMenu'
import ContactUs from './pages/ContactUs'
import NPSSurvey from './pages/NPSSurvey'
import NotFound from './pages/NotFound'
import GenericNotFound from './pages/GenericNotFound'
import ScrollToTop from './components/ScrollToTop'
import ForgotPasswordGuide from './pages/ForgotPasswordGuide'
import './App.css'
import SharingScreenExternal from './pages/gamification/SharingScreenExternal'

function App() {
  return (
    <Router>
      <ScrollToTop />
      <ToastProvider>
        <TenantProvider>
          <LanguageProvider>
            <ClientProvider>
              <CardProvider>
                <CardFetchSync />
                <CookieConsentManager />
                <AnalyticsManager />
                <MarketingPixelManager />
                <ABTestProvider>
                  <ABTestManager />
                  <OffersProvider>
                    <GamificationProvider>
                      <ThemeProvider>
                        <div className="App">
                          <Routes>
                              {/* Common unsubscribe routes intentionally render without tenant header/footer */}
                              <Route
                                path="/unsubscribe"
                                element={<EmailUnsubscribe />}
                              />

                              {/* Tenant-scoped routes with MainLayout wrapper */}
                              <Route
                                path="/:tenanturlname"
                                element={<MainLayout />}
                              >
                                {/* Default: show home page when only tenant name is in URL */}
                                <Route
                                  index
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={false}
                                    >
                                      <HomePage />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Public-only routes - redirect to home if authenticated */}
                                <Route
                                  path="signin"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={true}
                                    >
                                      <SignIn />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="create-account"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={true}
                                    >
                                      <CreateAccount />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="verify-email"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={true}
                                    >
                                      <VerifyEmail />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="verify/:token"
                                  element={<VerifyEmailToken />}
                                />
                                <Route
                                  path="verify/:verifytoken/:campaignid"
                                  element={<Verify />}
                                />
                                <Route
                                  path="forgot-password"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={true}
                                    >
                                      <ForgotPassword />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="magic-link"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={true}
                                    >
                                      <PasswordlessLogin />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="reset-password/:token"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={true}
                                    >
                                      <ResetPassword />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="migrate"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={true}
                                    >
                                      <MigrationRequest />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="set-password/:token"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={true}
                                    >
                                      <SetPassword />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Public routes - accessible to everyone */}
                                <Route
                                  path="offer/:offerId"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={false}
                                    >
                                      <OfferDetails />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Protected routes - require authentication */}
                                <Route
                                  path="myprofile"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      {/* <ProtectedRoute requireAuth={false}> */}
                                      <MyProfile />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="change-password"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <ChangePassword />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="change-email"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <ChangeEmail />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="change-email/pending"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={false}
                                    >
                                      <ChangeEmailPending />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="account-settings"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <AccountSettingFormMenu />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Tenant-scoped public pages */}
                                <Route path="faq" element={<FAQ />} />
                                <Route
                                  path="terms"
                                  element={<TermsAndConditions />}
                                />
                                <Route
                                  path="about"
                                  element={<AboutVoxPage />}
                                />
                                <Route
                                  path="cookiepolicy"
                                  element={<CookiePolicy />}
                                />
                                <Route
                                  path="servicepolicy"
                                  element={<ServicePolicy />}
                                />
                                <Route
                                  path="contact-us"
                                  element={<ContactUs />}
                                />
                                <Route
                                  path="unsubscribe/:euid/:distributorid"
                                  element={<EmailUnsubscribe />}
                                />
                                <Route
                                  path="unsubscribe"
                                  element={<EmailUnsubscribe />}
                                />

                                {/* Inquiry Form routes */}
                                <Route
                                  path="inquiry"
                                  element={<InquiryFormMenu />}
                                />
                                <Route
                                  path="inquiry/category/:categoryId"
                                  element={<InquiryCategoryForms />}
                                />
                                <Route
                                  path="inquiry/:formId"
                                  element={<InquiryForm />}
                                />

                                {/* ForgotPassword Guide Page routes */}
                                <Route
                                  path="ForgotPasswordGuide"
                                  element={<ForgotPasswordGuide />}
                                />

                                {/* Gamification routes */}
                                <Route
                                  path="gamification/spinwheel/:campaignId"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <SpinWheel />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="gamification/congratulation"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <CongratulationScreen />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="gamification/sharing"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={false}
                                    >
                                      <SharingScreen />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="gamification/stamprally/:campaignId"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <StampRally />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Email NPS Survey - public access */}
                                <Route
                                  path="nps-survey"
                                  element={
                                    <ProtectedRoute
                                      requireAuth={false}
                                      isPublicOnly={false}
                                    >
                                      <NPSSurvey />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Branded 404 - valid tenant slug, unknown sub-path */}
                                <Route path="*" element={<NotFound />} />
                              </Route>

                              {/* Health check endpoints - public access */}
                              <Route path="/health" element={<HealthCheck />} />
                              <Route
                                path="/health-check"
                                element={<HealthCheck />}
                              />

                              {/* Legacy login route - redirect to tenant-scoped signin */}
                              {/* <Route
                              path="/login"
                              element={
                                <Navigate to="/dev-demo/signin" replace />
                              }
                            /> */}

                              {/* Optional root-level public pages (keep only if you want non-tenant access) */}
                              <Route path="/faq" element={<FAQ />} />
                              <Route
                                path="/terms"
                                element={<TermsAndConditions />}
                              />
                              <Route path="/about" element={<AboutVoxPage />} />

                              <Route
                                path="/gamification/sharing/:teid/:txid/:type"
                                element={<SharingScreenExternal />}
                              />

                              {/* 404 - Not Found (invalid tenant slug or unknown top-level path) */}
                              <Route path="*" element={<GenericNotFound />} />
                          </Routes>
                        </div>
                      </ThemeProvider>
                    </GamificationProvider>
                  </OffersProvider>
                </ABTestProvider>
              </CardProvider>
            </ClientProvider>
          </LanguageProvider>
        </TenantProvider>
      </ToastProvider>
    </Router>
  )
}

export default App
